import {
  clientFilterLabels,
  clientFilters,
  formatDayKey,
  MAX_CLIENTS_PER_MESSAGE,
  MESSAGE_BODY_MAX,
  type ClientFilter,
  type ClientRow,
  type ClientsMessageRequest,
  type ClientsMessageResponse,
  type ClientsResponse,
  type Family,
} from "@teamna/shared";
import { useState } from "react";
import {
  Badge,
  Button,
  ChatIcon,
  ChipRow,
  DataTable,
  ErrorState,
  InlineError,
  Panel,
  SkeletonList,
  TextAreaField,
  showToast,
  type DataTableColumn,
} from "../../components/index.ts";
import { formatDate, formatDateTime } from "@teamna/web-client/lib/format.ts";
import { FamilyNameCell } from "./bits.tsx";
import { capital, type Words } from "./model.ts";

/**
 * The Clients table (Pilates studio shape, C1): where clients book for themselves, the members page
 * shows one row for each client with the pack, the sessions left, the end date, the last visit,
 * the next booking and the flags. The server filters the rows and counts each filter. The owner
 * ticks rows (or takes the whole filter) and sends one message into each private thread.
 *
 * The view takes plain props, so Storybook draws it in every state; `ParentsView` wires it.
 */

/** A client row with the family it belongs to (the face, the numbers and the hold). */
export type ClientTableRow = { client: ClientRow; family: Family };

/** Joins the rows of the filter with the families read, in the order of the rows. */
export function clientTableRows(items: readonly ClientRow[], families: readonly Family[]): ClientTableRow[] {
  const byId = new Map(families.map((family) => [family.id, family]));
  return items.flatMap((client) => {
    const family = byId.get(client.familyId);
    return family ? [{ client, family }] : [];
  });
}

/** The words of the message result: "Sent to 4. 2 are not in the app yet, so they got nothing." */
export function messageResultText(result: ClientsMessageResponse, words: Words): string {
  const parts = [`Sent to ${result.sent} ${result.sent === 1 ? words.member : words.members}.`];
  if (result.notJoined) {
    parts.push(`${result.notJoined} ${result.notJoined === 1 ? "is" : "are"} not in the app yet, so they got nothing.`);
  }
  if (result.limited) parts.push(`${result.limited} not sent: you sent many messages this hour. Send to them again later.`);
  return parts.join(" ");
}

export function clientColumns(timezone: string, words: Words): DataTableColumn<ClientTableRow>[] {
  return [
    {
      id: "name",
      header: capital(words.member),
      sortValue: (row) => row.client.name,
      cell: (row) => <FamilyNameCell family={row.family} name={row.client.name} />,
    },
    {
      id: "package",
      header: "Package",
      hideOnPhone: true,
      sortValue: (row) => row.client.packageName,
      cell: (row) =>
        row.client.packageName ? (
          <span className="text-ink" dir="auto">
            {row.client.packageName}
          </span>
        ) : (
          <span className="text-[13px] text-ink-3">No package</span>
        ),
    },
    {
      id: "sessions",
      header: "Sessions left",
      align: "end",
      sortValue: (row) => row.client.sessionsLeft,
      cell: (row) =>
        row.client.sessionsLeft === null ? (
          <span className="text-[13px] text-ink-3">None</span>
        ) : (
          <span className={`font-semibold tabular-nums ${row.client.sessionsLeft <= 2 ? "text-late" : "text-ink"}`}>{row.client.sessionsLeft}</span>
        ),
    },
    {
      id: "ends",
      header: "Ends on",
      sortValue: (row) => row.client.endsOn,
      cell: (row) => (row.client.endsOn ? <span className="whitespace-nowrap">{formatDayKey(row.client.endsOn)}</span> : null),
    },
    {
      id: "last",
      header: "Last visit",
      hideOnPhone: true,
      sortValue: (row) => row.client.lastVisitAt,
      cell: (row) =>
        row.client.lastVisitAt ? (
          <span className="whitespace-nowrap">{formatDate(row.client.lastVisitAt, timezone)}</span>
        ) : (
          <span className="text-[13px] text-ink-3">Not yet</span>
        ),
    },
    {
      id: "next",
      header: "Next booking",
      hideOnPhone: true,
      sortValue: (row) => row.client.nextBookingAt,
      cell: (row) =>
        row.client.nextBookingAt ? (
          <span className="whitespace-nowrap">{formatDateTime(row.client.nextBookingAt, timezone)}</span>
        ) : (
          <span className="text-[13px] text-ink-3">Nothing booked</span>
        ),
    },
    {
      id: "flags",
      header: "Flags",
      cell: (row) => <ClientFlags client={row.client} />,
    },
  ];
}

export function ClientFlags({ client }: { client: Pick<ClientRow, "intro" | "paused" | "owed"> }) {
  if (!client.intro && !client.paused && !client.owed) return null;
  return (
    <span className="flex flex-wrap gap-1.5">
      {client.intro ? <Badge tone="confirmed">Intro offer</Badge> : null}
      {client.paused ? <Badge tone="quiet">Paused</Badge> : null}
      {client.owed ? <Badge tone="late">Owes</Badge> : null}
    </span>
  );
}

export function ClientsList({
  response,
  rows,
  loading = false,
  error = null,
  onRetry,
  filter,
  onFilter,
  searched,
  ranked,
  timezone,
  words,
  onOpenClient,
  onMessage,
}: {
  /** The answer of the filter, or null before the first one. */
  response: ClientsResponse | null;
  /** The rows of the filter that the search keeps, with their families. */
  rows: ClientTableRow[];
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  filter: ClientFilter;
  onFilter: (filter: ClientFilter) => void;
  /** A search is typed. */
  searched: boolean;
  /** The search ranks the rows best first. */
  ranked: boolean;
  timezone: string;
  words: Words;
  onOpenClient: (familyId: string) => void;
  /** Missing: no message to many and no ticks (the front desk, T1). */
  onMessage?: (input: ClientsMessageRequest) => Promise<ClientsMessageResponse>;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [compose, setCompose] = useState<{ scope: "selected" | "filter" } | null>(null);

  function pick(next: ClientFilter) {
    setSelected(new Set());
    onFilter(next);
  }

  const counts = response?.counts;
  const chips = clientFilters.map((id) => (counts ? { id, label: clientFilterLabels[id], count: counts[id] } : { id, label: clientFilterLabels[id] }));
  const inFilter = counts?.[filter] ?? 0;
  // Ticks of rows the search or a new answer no longer shows do not count.
  const shownIds = new Set(rows.map((row) => row.client.familyId));
  const ticked = [...selected].filter((id) => shownIds.has(id));

  if (!response && loading) return <SkeletonList />;
  if (!response && error) return <ErrorState error={error} onRetry={onRetry} />;

  return (
    <div className="flex flex-col gap-4">
      <ChipRow options={chips} selectedId={filter} onSelect={(id) => pick(id as ClientFilter)} label="Show" wrap />
      {inFilter > 0 && onMessage ? (
        <div className="flex flex-wrap items-center gap-2" aria-label="Message">
          <Button variant="secondary" disabled={ticked.length === 0} onClick={() => setCompose({ scope: "selected" })}>
            <ChatIcon size={17} />
            {ticked.length ? `Message ${ticked.length} selected` : "Tick rows to message them"}
          </Button>
          <Button variant="ghost" disabled={inFilter > MAX_CLIENTS_PER_MESSAGE} onClick={() => setCompose({ scope: "filter" })}>
            Message all {inFilter} in {filter === "all" ? "the list" : `"${clientFilterLabels[filter]}"`}
          </Button>
        </div>
      ) : null}
      {error ? <InlineError error={error} /> : null}
      <DataTable
        caption={capital(words.members)}
        columns={clientColumns(timezone, words)}
        rows={rows}
        rowKey={(row) => row.client.familyId}
        rowLabel={(row) => row.client.name}
        onRowClick={(row) => onOpenClient(row.client.familyId)}
        {...(onMessage ? { selection: { selected, onChange: setSelected } } : {})}
        isLoading={loading && rows.length === 0}
        defaultSort={{ id: "name", direction: "asc" }}
        ranked={ranked}
        countLabel={(count) => `${count} ${count === 1 ? words.member : words.members}`}
        empty={{
          title: searched ? `No ${words.member} matches` : filter === "all" ? `No ${words.members} yet` : "Nothing here",
          message: searched
            ? "Try a name or the last digits of a number."
            : filter === "all"
              ? `Add your ${words.members} with "Add ${words.members}".`
              : `No ${words.member} is in "${clientFilterLabels[filter]}" today.`,
        }}
      />
      {compose && onMessage ? (
        <MessagePanel
          count={compose.scope === "selected" ? ticked.length : inFilter}
          words={words}
          onClose={() => setCompose(null)}
          onSend={async (body) => {
            const result = await onMessage(compose.scope === "selected" ? { familyIds: ticked, body } : { filter, body });
            setCompose(null);
            setSelected(new Set());
            showToast(messageResultText(result, words));
          }}
        />
      ) : null}
    </div>
  );
}

/** The message to many clients: one text, sent into the private thread of each, never a WhatsApp group. */
export function MessagePanel({
  count,
  words,
  onSend,
  onClose,
}: {
  count: number;
  words: Words;
  onSend: (body: string) => Promise<void>;
  onClose: () => void;
}) {
  const [body, setBody] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const text = body.trim();

  async function send() {
    setPending(true);
    setError(null);
    try {
      await onSend(text);
    } catch (caught) {
      setError(caught);
    } finally {
      setPending(false);
    }
  }

  return (
    <Panel
      open
      onClose={onClose}
      title={`Message ${count} ${count === 1 ? words.member : words.members}`}
      subtitle={`Each ${words.member} gets it in their own chat with you in the app. No WhatsApp group.`}
      actions={
        <>
          <Button loading={pending} disabled={!text} onClick={() => void send()}>
            Send
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <TextAreaField
          label="Message"
          hint={`A ${words.member} who is not in the app yet gets nothing.`}
          rows={5}
          value={body}
          maxLength={MESSAGE_BODY_MAX}
          onChange={(event) => setBody(event.target.value)}
          dir="auto"
        />
        {error ? <InlineError error={error} /> : null}
      </div>
    </Panel>
  );
}
